import { useEffect, useState, useMemo } from 'react'
import { usePerDiem } from '../hooks/useApi'
import { formatCurrency, getMonthYear } from '../lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { toast } from 'sonner'
import { ChevronLeft, ChevronRight, Home, Truck, Sun, RotateCcw } from 'lucide-react'
import { cn } from '../lib/utils'
import { analytics } from '../lib/analytics'

const DAY_TYPES = {
  home: { label: 'Home', icon: Home, color: 'text-muted-foreground', bg: '' },
  partial: { label: 'Partial', icon: Sun, color: 'text-amber-500', bg: 'bg-amber-500/20 border-2 border-amber-500' },
  full: { label: 'Full Day', icon: Truck, color: 'text-green-500', bg: 'bg-green-500/20 border-2 border-green-500' }
}

export default function PerDiem() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [perDiemDays, setPerDiemDays] = useState([])
  const [settings, setSettings] = useState({ partial_rate: 60, full_rate: 80 })
  const [pendingDeparture, setPendingDeparture] = useState(null) // Track first tap (departure)
  
  const { fetchDays, fetchSettings, setDay, removeDay, loading } = usePerDiem()

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1

  useEffect(() => {
    loadData()
  }, [year, month])

  const loadData = async () => {
    try {
      const [days, settingsData] = await Promise.all([
        fetchDays(year, month),
        fetchSettings()
      ])
      setPerDiemDays(days)
      setSettings(settingsData)
      
      // Check if there's an existing pending departure (partial day with no following partial)
      const partialDays = days.filter(d => d.day_type === 'partial').sort((a, b) => a.date.localeCompare(b.date))
      if (partialDays.length % 2 === 1) {
        // Odd number of partial days means there's an open trip
        setPendingDeparture(partialDays[partialDays.length - 1].date)
      } else {
        setPendingDeparture(null)
      }
    } catch (err) {
      console.error('Failed to load per diem data:', err)
    }
  }

  const daysInMonth = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1)
    const lastDay = new Date(year, month, 0)
    const days = []
    
    // Add empty slots for days before the first of the month
    const startDayOfWeek = firstDay.getDay()
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null)
    }
    
    // Add all days of the month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const dayData = perDiemDays.find(pd => pd.date === dateStr)
      days.push({
        day: d,
        date: dateStr,
        type: dayData?.day_type || 'home'
      })
    }
    
    return days
  }, [year, month, perDiemDays])

  // Generate all dates between two date strings (exclusive of start and end)
  const getDatesBetween = (startDateStr, endDateStr) => {
    const dates = []
    
    // Parse dates as local dates (avoid timezone issues)
    const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number)
    const [endYear, endMonth, endDay] = endDateStr.split('-').map(Number)
    
    const start = new Date(startYear, startMonth - 1, startDay)
    const end = new Date(endYear, endMonth - 1, endDay)
    
    // Start from day after departure
    const current = new Date(start)
    current.setDate(current.getDate() + 1)
    
    // Continue until we reach the return day (not including it)
    while (current.getTime() < end.getTime()) {
      const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`
      dates.push(dateStr)
      current.setDate(current.getDate() + 1)
    }
    
    return dates
  }

  const handleDayClick = async (dayInfo) => {
    if (!dayInfo) return
    
    const clickedDate = dayInfo.date
    
    // If clicking on an already marked day, offer to clear it
    if (dayInfo.type !== 'home') {
      // Clear this day and potentially reset trip
      try {
        await removeDay(clickedDate)
        setPerDiemDays(prev => prev.filter(d => d.date !== clickedDate))
        
        if (pendingDeparture === clickedDate) {
          setPendingDeparture(null)
          toast.success('Trip cancelled')
        } else {
          toast.success('Day cleared')
        }
      } catch (err) {
        toast.error('Failed to clear day')
      }
      return
    }
    
    // If no pending departure, this is the START of a trip
    if (!pendingDeparture) {
      try {
        await setDay({ date: clickedDate, day_type: 'partial' })
        setPerDiemDays(prev => [...prev, { date: clickedDate, day_type: 'partial' }])
        setPendingDeparture(clickedDate)
        toast.success('Departure day marked! Tap your return day to complete the trip.')
      } catch (err) {
        toast.error('Failed to mark departure')
      }
      return
    }
    
    // If there IS a pending departure, this is the RETURN day
    // Fill in the departure (already done), all days between as full, and return as partial
    try {
      const departureDate = pendingDeparture
      const returnDate = clickedDate
      
      // Ensure return is after departure
      if (returnDate <= departureDate) {
        toast.error('Return day must be after departure day')
        return
      }
      
      // Get all dates between departure and return
      const fullDays = getDatesBetween(departureDate, returnDate)
      
      // Mark return day as partial
      await setDay({ date: returnDate, day_type: 'partial' })
      
      // Mark all days between as full
      for (const fullDate of fullDays) {
        await setDay({ date: fullDate, day_type: 'full' })
      }
      
      // Update local state
      setPerDiemDays(prev => {
        const updated = [...prev]
        // Add return day
        updated.push({ date: returnDate, day_type: 'partial' })
        // Add full days
        fullDays.forEach(fd => {
          if (!updated.find(d => d.date === fd)) {
            updated.push({ date: fd, day_type: 'full' })
          }
        })
        return updated
      })
      
      setPendingDeparture(null)
      
      const totalDays = fullDays.length + 2 // +2 for departure and return partial days
      analytics.perDiemEntered(totalDays)
      toast.success(`Trip complete! ${totalDays} days marked (${fullDays.length} full + 2 partial)`)
      
    } catch (err) {
      toast.error('Failed to complete trip')
    }
  }

  const handleClearTrip = async () => {
    if (pendingDeparture) {
      try {
        await removeDay(pendingDeparture)
        setPerDiemDays(prev => prev.filter(d => d.date !== pendingDeparture))
        setPendingDeparture(null)
        toast.success('Pending trip cancelled')
      } catch (err) {
        toast.error('Failed to cancel trip')
      }
    }
  }

  const goToPrevMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1))
  }

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month, 1))
  }

  // Calculate totals
  const partialCount = perDiemDays.filter(d => d.day_type === 'partial').length
  const fullCount = perDiemDays.filter(d => d.day_type === 'full').length
  const monthTotal = (partialCount * settings.partial_rate) + (fullCount * settings.full_rate)

  return (
    <div className="space-y-6 animate-fade-in" data-testid="per-diem-page">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight font-['Barlow_Condensed']">
          PER DIEM CALENDAR
        </h1>
        <p className="text-muted-foreground">
          Tap departure day, then tap return day
        </p>
      </div>

      {/* Instructions & Status */}
      <Card className={pendingDeparture ? 'border-amber-500' : ''}>
        <CardContent className="p-4">
          {pendingDeparture ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="font-medium text-amber-500">Trip in progress</p>
                  <p className="text-sm text-muted-foreground">
                    Departed: {new Date(pendingDeparture).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {' '}- Tap your return day
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClearTrip}
                className="text-muted-foreground"
                data-testid="cancel-trip-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-4 justify-center">
              {Object.entries(DAY_TYPES).map(([key, { label, icon: Icon, color }]) => (
                <div key={key} className="flex items-center gap-2">
                  <Icon className={cn("w-4 h-4", color)} />
                  <span className="text-sm">{label}</span>
                  {key !== 'home' && (
                    <span className="text-xs text-muted-foreground">
                      ({formatCurrency(key === 'partial' ? settings.partial_rate : settings.full_rate, 0)})
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calendar */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={goToPrevMonth} data-testid="prev-month-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <CardTitle className="text-xl font-['Barlow_Condensed']">
              {getMonthYear(currentDate).toUpperCase()}
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={goToNextMonth} data-testid="next-month-btn">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-center text-xs font-semibold text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {daysInMonth.map((dayInfo, index) => {
              const isPendingDeparture = dayInfo && dayInfo.date === pendingDeparture
              return (
                <button
                  key={index}
                  onClick={() => handleDayClick(dayInfo)}
                  disabled={!dayInfo}
                  className={cn(
                    "aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all touch-target relative",
                    dayInfo ? "hover:bg-secondary cursor-pointer" : "",
                    dayInfo && DAY_TYPES[dayInfo.type]?.bg,
                    isPendingDeparture && "ring-2 ring-amber-500 ring-offset-2 ring-offset-background"
                  )}
                  data-testid={dayInfo ? `day-${dayInfo.date}` : undefined}
                >
                  {dayInfo?.day}
                  {isPendingDeparture && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full animate-pulse" />
                  )}
                </button>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Monthly Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase">
            Monthly Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Sun className="w-4 h-4 text-amber-500" />
              <span>Partial Days (Departure/Return)</span>
            </div>
            <div className="text-right">
              <span className="font-semibold">{partialCount}</span>
              <span className="text-muted-foreground ml-2">
                × {formatCurrency(settings.partial_rate, 0)} = {formatCurrency(partialCount * settings.partial_rate, 0)}
              </span>
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-green-500" />
              <span>Full Days (On Road)</span>
            </div>
            <div className="text-right">
              <span className="font-semibold">{fullCount}</span>
              <span className="text-muted-foreground ml-2">
                × {formatCurrency(settings.full_rate, 0)} = {formatCurrency(fullCount * settings.full_rate, 0)}
              </span>
            </div>
          </div>
          
          <div className="border-t border-border pt-3 flex justify-between items-center">
            <span className="font-semibold">Monthly Total</span>
            <span className="text-xl font-bold text-primary kpi-value">
              {formatCurrency(monthTotal, 0)}
            </span>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-center text-muted-foreground">
        Tap a marked day to clear it. Clearing departure cancels the trip.
      </p>
    </div>
  )
}
